// FILE: src/components/ui/index.jsx
// Shared building blocks for every page (plan WP-8 task 1, matrix 10.2):
// one page header with the page's single <h1>, and one look for empty,
// loading and error states. Errors always go through messageFor (B-18):
// pass the server's error (code, Error or payload), never prose from it.
import React from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Link,
  Skeleton,
  Stack,
  Typography
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import { errorMessage } from "../../i18n/errors";

/**
 * The top of every page: optional breadcrumbs, the page's only <h1>,
 * a sentence about what the page is for, and the page's actions.
 */
export function PageHeader({ title, eyebrow, description, actions, breadcrumbs, children }) {
  return (
    <Box component="header" sx={{ mb: { xs: 3, md: 4 } }}>
      {breadcrumbs?.length > 0 && (
        <Breadcrumbs aria-label="Breadcrumb" sx={{ mb: 1.5 }}>
          {breadcrumbs.map((b, i) =>
            b.to && i < breadcrumbs.length - 1 ? (
              <Link key={b.label} component={RouterLink} to={b.to} color="text.secondary">
                {b.label}
              </Link>
            ) : (
              <Typography key={b.label} color="text.primary" aria-current="page">
                {b.label}
              </Typography>
            )
          )}
        </Breadcrumbs>
      )}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{ alignItems: { xs: "flex-start", sm: "flex-end" }, justifyContent: "space-between" }}
      >
        <Box sx={{ minWidth: 0 }}>
          {eyebrow && (
            <Typography sx={{ color: "text.secondary", fontWeight: 600, mb: 0.5 }}>{eyebrow}</Typography>
          )}
          <Typography variant="h1">{title}</Typography>
          {description && (
            <Typography sx={{ color: "text.secondary", mt: 1, maxWidth: "70ch" }}>{description}</Typography>
          )}
        </Box>
        {actions && (
          <Stack direction="row" spacing={1.5} sx={{ flexShrink: 0, flexWrap: "wrap", rowGap: 1.5 }}>
            {actions}
          </Stack>
        )}
      </Stack>
      {children}
    </Box>
  );
}

/** A titled card section; the title is an <h2> unless told otherwise. */
export function Section({ title, description, action, children, headingLevel = 2, id, sx, contentSx }) {
  const headingId = id ? `${id}-title` : undefined;
  return (
    <Card component="section" aria-labelledby={headingId} sx={sx}>
      <CardContent sx={contentSx}>
        {(title || action) && (
          <Stack
            direction="row"
            spacing={2}
            sx={{ alignItems: "flex-start", justifyContent: "space-between", mb: description ? 0.5 : 2 }}
          >
            {title && (
              <Typography id={headingId} variant="h3" component={`h${headingLevel}`}>
                {title}
              </Typography>
            )}
            {action}
          </Stack>
        )}
        {description && <Typography sx={{ color: "text.secondary", mb: 2 }}>{description}</Typography>}
        {children}
      </CardContent>
    </Card>
  );
}

/** Nothing to show yet — always says what to do next. */
export function EmptyState({ title, description, action, icon: Icon = InboxOutlinedIcon, compact = false }) {
  return (
    <Box
      role="status"
      sx={{
        textAlign: "center",
        py: compact ? 3 : 6,
        px: 2,
        border: "1px dashed",
        borderColor: "divider",
        borderRadius: 3
      }}
    >
      <Icon aria-hidden sx={{ fontSize: 40, color: "text.secondary", mb: 1 }} />
      <Typography variant="h4" component="p" sx={{ mb: 0.5 }}>
        {title}
      </Typography>
      {description && <Typography sx={{ color: "text.secondary", maxWidth: "52ch", mx: "auto" }}>{description}</Typography>}
      {action && <Box sx={{ mt: 2 }}>{action}</Box>}
    </Box>
  );
}

/**
 * Loading placeholder. `variant`: "page" (header + cards), "cards", "table"
 * or "inline". Announced politely to screen readers once.
 */
export function LoadingState({ label = "Loading…", variant = "cards", rows = 4 }) {
  if (variant === "inline") {
    return (
      <Box role="status" aria-live="polite" sx={{ py: 2 }}>
        <LinearProgress aria-label={label} />
        <Typography sx={{ mt: 1, color: "text.secondary" }}>{label}</Typography>
      </Box>
    );
  }
  return (
    <Box role="status" aria-live="polite" aria-busy="true">
      <Typography sx={visuallyHidden}>{label}</Typography>
      {variant === "page" && (
        <Box sx={{ mb: 4 }}>
          <Skeleton variant="text" width="30%" height={28} />
          <Skeleton variant="text" width="45%" height={48} />
        </Box>
      )}
      {(variant === "cards" || variant === "page") && (
        <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" } }}>
          {Array.from({ length: Math.min(rows, 6) }).map((_, i) => (
            <Skeleton key={i} variant="rounded" height={132} sx={{ borderRadius: 3.5 }} />
          ))}
        </Box>
      )}
      {variant === "table" && (
        <Stack spacing={1}>
          <Skeleton variant="rounded" height={48} />
          {Array.from({ length: rows }).map((_, i) => (
            <Skeleton key={i} variant="rounded" height={56} />
          ))}
        </Stack>
      )}
    </Box>
  );
}

/** A failed load: plain words from messageFor, and a way to try again. */
export function ErrorState({ error, title = "We couldn't load this", onRetry, compact = false }) {
  return (
    <Alert
      severity="error"
      icon={<ErrorOutlineIcon />}
      role="alert"
      sx={{ my: compact ? 1 : 2 }}
      action={
        onRetry ? (
          <Button color="inherit" startIcon={<RefreshIcon />} onClick={onRetry} sx={{ fontWeight: 650 }}>
            Try again
          </Button>
        ) : null
      }
    >
      {!compact && <AlertTitle>{title}</AlertTitle>}
      {errorMessage(error)}
    </Alert>
  );
}

/** A figure with a label, an optional bar (always clamped 0–100) and a note. */
export function StatCard({ label, value, note, progress, progressLabel, icon: Icon, tone = "primary", testId, children }) {
  const pct = progress == null ? null : clampPct(progress);
  return (
    <Card sx={{ height: "100%" }} data-testid={testId}>
      <CardContent>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", mb: 1.5 }}>
          {Icon && (
            <Box
              aria-hidden
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2.5,
                display: "grid",
                placeItems: "center",
                bgcolor: tone === "warning" ? "zun.warningTint" : tone === "error" ? "zun.errorTint" : "zun.primaryTint",
                color: `${tone}.main`
              }}
            >
              <Icon />
            </Box>
          )}
          <Typography component="h3" sx={{ fontWeight: 600, color: "text.secondary" }}>
            {label}
          </Typography>
        </Stack>
        <Typography component="p" sx={{ fontSize: "1.75rem", fontWeight: 700, lineHeight: 1.2, letterSpacing: "-0.01em" }}>
          {value}
        </Typography>
        {pct != null && (
          <LinearProgress
            variant="determinate"
            value={pct}
            color={tone === "warning" ? "warning" : tone === "error" ? "error" : "primary"}
            aria-label={progressLabel || label}
            sx={{ mt: 1.5 }}
          />
        )}
        {note && <Typography sx={{ color: "text.secondary", mt: 1 }}>{note}</Typography>}
        {children}
      </CardContent>
    </Card>
  );
}

/**
 * A status as words + icon, never colour alone (MTO 5.4). Pass `label`
 * already humanised (see src/i18n/labels.js).
 */
export function StatusChip({ label, tone = "default", icon, size = "medium", ...rest }) {
  const styles = {
    success: { bgcolor: "zun.primaryTint", color: "primary.main" },
    info: { bgcolor: "zun.infoTint", color: "info.main" },
    warning: { bgcolor: "zun.warningTint", color: "warning.main" },
    error: { bgcolor: "zun.errorTint", color: "error.main" },
    default: { bgcolor: "action.hover", color: "text.secondary" }
  }[tone];
  return (
    <Chip
      label={label}
      icon={icon || undefined}
      size={size}
      sx={{ ...styles, "& .MuiChip-icon": { color: "inherit" }, border: "1px solid", borderColor: "divider" }}
      {...rest}
    />
  );
}

export function clampPct(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, n));
}

/** A row of label/value pairs (definition list) for detail pages. */
export function DetailList({ items, columns = 2 }) {
  return (
    <Box
      component="dl"
      sx={{
        m: 0,
        display: "grid",
        gap: 2,
        gridTemplateColumns: { xs: "1fr", sm: `repeat(${columns}, minmax(0, 1fr))` }
      }}
    >
      {items
        .filter((it) => it && it.label)
        .map((it) => (
          <Box key={it.label} sx={{ minWidth: 0 }}>
            <Typography component="dt" sx={{ color: "text.secondary", fontWeight: 600 }}>
              {it.label}
            </Typography>
            <Typography component="dd" sx={{ m: 0, overflowWrap: "anywhere" }}>
              {it.value ?? "—"}
            </Typography>
          </Box>
        ))}
    </Box>
  );
}

export const visuallyHidden = {
  border: 0,
  clip: "rect(0 0 0 0)",
  height: "1px",
  margin: "-1px",
  overflow: "hidden",
  padding: 0,
  position: "absolute",
  whiteSpace: "nowrap",
  width: "1px"
};
